import { yupResolver } from '@hookform/resolvers/yup';
import {
  useEditHaulRatesMutation,
  useGetHaulRatesQuery,
} from 'common/api/endpoints/harvestData';
import { handleError } from 'common/api/utils/handleError';
import { DataTable, DataTableProps } from 'common/components/DataTable';
import * as notifier from 'common/services/notification';
import {
  HaulRateTableRows,
  useHaulRatesTableData,
} from 'features/settings-view/hooks/useHaulRatesTableData';
import { FC, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { ratesSchema } from 'utils/schemas/settings';
import { formatDefaultRates, toEditHaulRatesDto } from '../utils/functions';

export type HaulRateValues = Record<string, number | null>;

export const HaulRates: FC = () => {
  /**
   * Because haul rates don't have periods and we don't care about surcharges
   * in the 'Haul Rate' tab context, any date can be used to fetch the only
   * existing set of haul rates by range.
   */
  const dummyDate = '2025-01-01';
  const {
    data: rates,
    isLoading,
    isFetching,
    error,
  } = useGetHaulRatesQuery({ pickDate: dummyDate });
  const [editHaulRates, { isLoading: isSubmitting }] =
    useEditHaulRatesMutation();
  const { columns, data, isEditing, setIsEditing } = useHaulRatesTableData(
    rates || [],
    isSubmitting,
  );

  const defaultValues = useMemo(() => formatDefaultRates(rates || []), [rates]);

  const methods = useForm<HaulRateValues>({
    values: defaultValues,
    resolver: yupResolver(ratesSchema),
    mode: 'all',
  });
  const {
    reset,
    formState: { isDirty },
  } = methods;

  const closeForm = () => {
    reset();
    setIsEditing(false);
  };

  const onSubmit = async (data: HaulRateValues): Promise<void> => {
    if (!isDirty) {
      // No values to save.
      closeForm();
      return;
    }

    const dto = toEditHaulRatesDto(data, defaultValues);

    try {
      await editHaulRates(dto).unwrap();
      notifier.showSuccessMessage(`Haul rates updated successfully.`);
      closeForm();
    } catch (e) {
      handleError(e, `Unable to edit haul rates.`);
    }
  };

  const tableProps: DataTableProps<HaulRateTableRows, HaulRateValues> = {
    columns,
    data,
    isLoading,
    isFetching,
    maxTableWidth: '700px',
    formProps: {
      methods,
      onSubmit,
      onCancel: closeForm,
      isEditing,
      setIsEditing,
      isDirty,
      isSubmitting,
    },
  };

  useEffect(() => {
    if (error) {
      handleError(error, 'Unable to fetch haul rates.');
    }
  }, [error]);

  return <DataTable {...tableProps} />;
};
